import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, LinearProgress,
  CircularProgress, Tooltip as MuiTooltip, Paper,
} from '@mui/material';
import {
  BarChart3, TrendingUp, Users, Briefcase, DollarSign,
  Info, TrendingDown, Activity,
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { api } from '../context/AuthContext';

const MotionCard = motion(Card);

/* ── Data Helpers ──────────────────────────────────────────────────────────── */

const getMonthLabel = (date) => {
  return new Date(date).toLocaleDateString('en-US', { month: 'short' });
};

const getLast6Months = () => {
  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: getMonthLabel(d),
      value: 0
    });
  }
  return months;
};

/* ── Components ────────────────────────────────────────────────────────────── */

function InfoTooltip({ text }) {
  return (
    <MuiTooltip
      title={<Typography sx={{ fontSize: '0.76rem', lineHeight: 1.55, maxWidth: 300, p: 0.5 }}>{text}</Typography>}
      arrow
      placement="top"
      componentsProps={{
        tooltip: { sx: { bgcolor: '#1e293b', borderRadius: '10px', py: 1, px: 1.5, maxWidth: 320 } },
        arrow: { sx: { color: '#1e293b' } },
      }}
    >
      <Box component="span" sx={{
        display: 'inline-flex', cursor: 'help', color: '#cbd5e1', ml: 0.5,
        verticalAlign: 'middle', '&:hover': { color: '#1e3a8a' }, transition: 'color 0.15s',
      }}>
        <Info size={13} />
      </Box>
    </MuiTooltip>
  );
}

function StatCard({ icon, label, value, color = '#1E3A8A', delay = 0, tooltip }) {
  return (
    <MotionCard
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay }}
      elevation={0}
      sx={{
        borderRadius: '16px', border: '1px solid #f1f5f9',
        borderBottom: `4px solid ${color}`,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        height: '100%',
        '&:hover': { 
          boxShadow: '0 8px 24px rgba(30,58,138,0.12)', 
          transform: 'translateY(-4px)' 
        },
        transition: 'all 0.3s ease',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: '12px',
            background: `${color}18`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color,
          }}>
            {icon}
          </Box>
          {tooltip && <InfoTooltip text={tooltip} />}
        </Box>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: '#111827', lineHeight: 1 }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600, mt: 0.5 }}>{label}</Typography>
      </CardContent>
    </MotionCard>
  );
}

function ChartCard({ title, icon, children, delay = 0 }) {
  return (
    <MotionCard
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      elevation={0}
      sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', background: '#fff', height: '100%' }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Box sx={{ color: '#1e3a8a', display: 'flex' }}>{icon}</Box>
          <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '1rem' }}>{title}</Typography>
        </Box>
        <Box sx={{ width: '100%', height: 250 }}>
          {children}
        </Box>
      </CardContent>
    </MotionCard>
  );
}

/* ── SectionHeader ─────────────────────────────────────────────────────────── */
function SectionHeader({ icon, title, description, tooltip }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icon && <Box sx={{ color: '#1e3a8a', display: 'flex' }}>{icon}</Box>}
        <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '1rem' }}>{title}</Typography>
        {tooltip && <InfoTooltip text={tooltip} />}
      </Box>
      {description && (
        <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5 }}>{description}</Typography>
      )}
    </Box>
  );
}

/* ── Main Component ────────────────────────────────────────────────────────── */

export default function SuperAdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    users: [],
    jobs: [],
    applications: [],
    payments: [],
    admins: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, jobsRes, appsRes, paymentsRes, adminsRes] = await Promise.all([
          api.get('/super-admin/users'),
          api.get('/super-admin/jobs'),
          api.get('/super-admin/interviews'),
          api.get('/payment/super-admin/payments'),
          api.get('/super-admin/admins')
        ]);

        setData({
          users: Array.isArray(usersRes.data) ? usersRes.data : [],
          jobs: Array.isArray(jobsRes.data) ? jobsRes.data : [],
          applications: Array.isArray(appsRes.data) ? appsRes.data : [],
          payments: Array.isArray(paymentsRes.data) ? paymentsRes.data : [],
          admins: Array.isArray(adminsRes.data) ? adminsRes.data : []
        });
      } catch (err) {
        console.error('Failed to fetch analytics data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const isCurrentMonth = (dateStr) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    };

    // KPI Calculations
    const totalRevenue = data.payments.reduce((acc, p) => p.status === 'captured' ? acc + (p.amount / 100) : acc, 0);
    const monthlyRevenue = data.payments.reduce((acc, p) => (p.status === 'captured' && isCurrentMonth(p.created_at)) ? acc + (p.amount / 100) : acc, 0);
    
    const monthlyNewUsers = data.users.filter(u => isCurrentMonth(u.created_at)).length;
    const monthlyJobs = data.jobs.filter(j => isCurrentMonth(j.created_at)).length;
    const monthlyApps = data.applications.filter(a => isCurrentMonth(a.created_at || a.createdAt)).length;

    // Trends (Last 6 Months)
    const months = getLast6Months();
    
    const revenueTrend = months.map(m => {
      const rev = data.payments.reduce((acc, p) => {
        if (p.status !== 'captured') return acc;
        const d = new Date(p.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return key === m.key ? acc + (p.amount / 100) : acc;
      }, 0);
      return { month: m.label, revenue: rev };
    });

    const userTrend = months.map(m => {
      const count = data.users.filter(u => {
        const d = new Date(u.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return key === m.key;
      }).length;
      return { month: m.label, users: count };
    });

    const jobTrend = months.map(m => {
      const count = data.jobs.filter(j => {
        const d = new Date(j.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return key === m.key;
      }).length;
      return { month: m.label, jobs: count };
    });

    const appTrend = months.map(m => {
      const count = data.applications.filter(a => {
        const d = new Date(a.created_at || a.createdAt);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return key === m.key;
      }).length;
      return { month: m.label, applications: count };
    });

    // Revenue Distribution
    const revDist = data.payments.reduce((acc, p) => {
      if (p.status !== 'captured') return acc;
      const plan = p.plan_code || 'Other';
      acc[plan] = (acc[plan] || 0) + (p.amount / 100);
      return acc;
    }, {});
    const revenueDistribution = Object.keys(revDist).map(key => ({ name: key, value: revDist[key] }));

    return {
      totalUsers: data.users.length,
      totalJobs: data.jobs.length,
      totalApplications: data.applications.length,
      totalRevenue: Math.round(totalRevenue),
      monthlyRevenue: Math.round(monthlyRevenue),
      monthlyNewUsers,
      monthlyJobs,
      monthlyApps,
      revenueTrend,
      userTrend,
      jobTrend,
      appTrend,
      revenueDistribution,
      totalRecruiters: data.admins.length
    };
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const COLORS = ['#1e3a8a', '#3b82f6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        {/*<Typography variant="h2" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>*/}
        <Typography
            variant="h4"
            sx={{
              color: "#1E3A8A",
              fontWeight: "bold",
              fontSize: { xs: "1.5rem", md: "2rem" }
            }}
        >
          Super Admin Analytics
        </Typography>
        <Typography sx={{ color: '#6b7280', fontSize: '0.9rem' }}>
          Comprehensive platform performance overview and growth metrics.
        </Typography>
      </Box>

      {/* KPI Cards */}
      <SectionHeader 
        icon={<BarChart3 size={18} />} 
        title="Key Performance Indicators" 
        description="Real-time platform metrics calculated from all system data including users, jobs, applications and revenue."
      />
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Users size={20} />} label="Total Users" value={stats.totalUsers} delay={0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Briefcase size={20} />} label="Total Jobs" value={stats.totalJobs} delay={0.05} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Activity size={20} />} label="Total Applications" value={stats.totalApplications} delay={0.1} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<DollarSign size={20} />} label="Total Revenue" value={`₹${stats.totalRevenue}`} color="#10b981" delay={0.15} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Users size={20} />} label="Monthly Users" value={stats.monthlyNewUsers} delay={0.2} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Briefcase size={20} />} label="Monthly Jobs Posted" value={stats.monthlyJobs} delay={0.25} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Activity size={20} />} label="Monthly Applications" value={stats.monthlyApps} delay={0.3} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<TrendingUp size={20} />} label="Monthly Revenue" value={`₹${stats.monthlyRevenue}`} color="#10b981" delay={0.35} />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Revenue Trend" icon={<TrendingUp size={18} />}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.revenueTrend}>
                <defs>
                  <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(value) => `₹${value}`} />
                <ReTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#colorRev)" strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="User Growth" icon={<Users size={18} />}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.userTrend}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1e3a8a" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <ReTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="users" stroke="#1e3a8a" fillOpacity={1} fill="url(#colorUsers)" strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Job Posting Trend" icon={<Briefcase size={18} />}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.jobTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <ReTooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="jobs" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Application Trend" icon={<Activity size={18} />}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.appTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <ReTooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="applications" fill="#ec4899" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Row 3: Revenue Distribution & Platform Growth Summary */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Revenue Distribution" icon={<DollarSign size={18} />}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.revenueDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {stats.revenueDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Platform Growth Summary" icon={<TrendingUp size={18} />}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Users</Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>{stats.totalUsers}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min(100, (stats.totalUsers / 100) * 100)} sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#1e3a8a' } }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Recruiters</Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>{stats.totalRecruiters}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min(100, (stats.totalRecruiters / 20) * 100)} sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Jobs</Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>{stats.totalJobs}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min(100, (stats.totalJobs / 500) * 100)} sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#ec4899' } }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Applications</Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>{stats.totalApplications}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min(100, (stats.totalApplications / 1000) * 100)} sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b' } }} />
              </Box>
            </Box>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
